/**
 * CR412: Hook for fetching/publishing deal card config via RPC.
 * 
 * Wraps the get_published_deal_card_config and publish_deal_card_config RPCs.
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

// ================================================================
// TYPES
// ================================================================

export interface PublishedDealCardConfig {
  environment: string;
  deal_type: string;
  active_version_id: string;
  config_json: DealCardPublishedConfigJson;
  created_at: string;
  created_by: string | null;
  source_version_id: string | null;
}

/** The canonical combined config shape stored in config_json */
export interface DealCardPublishedConfigJson {
  data_elements?: Record<string, unknown>;
  components?: Record<string, unknown>;
  meta?: {
    schemaVersion: number;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface PublishResult {
  environment: string;
  deal_type: string;
  active_version_id: string;
  created_at: string;
  created_by: string | null;
  source_version_id: string | null;
}

// ================================================================
// HOOK
// ================================================================

export function usePublishedDealCardConfig() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<PublishedDealCardConfig | null>(null);

  /**
   * Fetch the current published config for (environment, deal_type).
   * Returns null if no config is published yet.
   */
  const fetchPublished = useCallback(async (
    environment: string,
    dealType: string,
  ): Promise<PublishedDealCardConfig | null> => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcError } = await supabase.rpc(
        "get_published_deal_card_config",
        { p_environment: environment, p_deal_type: dealType },
      );

      if (rpcError) {
        setError(rpcError.message);
        console.error("[usePublishedDealCardConfig] fetch error:", rpcError);
        return null;
      }

      if (!data || (Array.isArray(data) && data.length === 0)) {
        setConfig(null);
        return null;
      }

      const row = Array.isArray(data) ? data[0] : data;
      const parsed: PublishedDealCardConfig = {
        environment: row.environment,
        deal_type: row.deal_type,
        active_version_id: row.active_version_id,
        config_json: (typeof row.config_json === "string"
          ? JSON.parse(row.config_json)
          : row.config_json) as DealCardPublishedConfigJson,
        created_at: row.created_at,
        created_by: row.created_by ?? null,
        source_version_id: row.source_version_id ?? null,
      };

      setConfig(parsed);
      return parsed;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(msg);
      console.error("[usePublishedDealCardConfig] unexpected error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Publish a new config version. Returns the publish result or null on error.
   * Pass expectedActiveVersionId for optimistic concurrency control.
   */
  const publish = useCallback(async (
    environment: string,
    dealType: string,
    configJson: DealCardPublishedConfigJson,
    opts?: {
      sourceVersionId?: string;
      expectedActiveVersionId?: string;
    },
  ): Promise<PublishResult | null> => {
    setLoading(true);
    setError(null);
    try {
      // CR412 C.1: Log session state before RPC call
      const { data: { session } } = await supabase.auth.getSession();
      console.log("[CR412] session before publish", {
        hasSession: !!session,
        userId: session?.user?.id,
        expiresAt: session?.expires_at,
      });

      // Build RPC args — always use the 5-arg version with concurrency guard
      const rpcArgs: Record<string, unknown> = {
        p_environment: environment,
        p_deal_type: dealType,
        p_config_json: configJson as unknown as Json,
        p_source_version_id: opts?.sourceVersionId ?? null,
        p_expected_active_version_id: opts?.expectedActiveVersionId ?? null,
      };

      console.log("[CR412] calling publish_deal_card_config", {
        environment, dealType,
        hasSourceVersion: !!opts?.sourceVersionId,
        expectedVersion: opts?.expectedActiveVersionId ?? "(none — first publish)",
        configKeys: Object.keys(configJson),
      });

      const { data, error: rpcError } = await supabase.rpc(
        "publish_deal_card_config" as any,
        rpcArgs as any,
      );

      console.log("[CR412] rpc result", { data, error: rpcError });

      if (rpcError) {
        // Check for concurrency conflict
        if (rpcError.message?.includes("CONFLICT:")) {
          setError("CONFLICT");
          return null;
        }
        setError(rpcError.message);
        console.error("[usePublishedDealCardConfig] publish error:", rpcError);
        return null;
      }

      const row = Array.isArray(data) ? data[0] : data;
      const result: PublishResult = {
        environment: row.environment,
        deal_type: row.deal_type,
        active_version_id: row.active_version_id,
        created_at: row.created_at,
        created_by: row.created_by ?? null,
        source_version_id: row.source_version_id ?? null,
      };

      // Update local state with the new config
      setConfig({
        ...result,
        config_json: configJson,
      });

      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(msg);
      console.error("[usePublishedDealCardConfig] publish error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    config,
    loading,
    error,
    fetchPublished,
    publish,
    /** Clear error state */
    clearError: useCallback(() => setError(null), []),
  };
}
